import React, { useLayoutEffect, useRef, useState } from 'react';
import { Admin, Resource, CustomRoutes, defaultTheme, Layout, SidebarToggleButton, LoadingIndicator } from 'react-admin';
import { Route } from 'react-router-dom';
import { createTheme } from '@mui/material/styles';
import { Box, AppBar as MuiAppBar } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import MailIcon from '@mui/icons-material/Mail';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import authProvider from './authProvider';
import dataProvider from './dataProvider';
import { UserList, UserEdit, UserCreate, UserShow } from './resources/users';
import { InvitationList, InvitationCreate, InvitationShow } from './resources/invitations';
import { OAuthApplicationList, OAuthApplicationCreate, OAuthApplicationEdit, OAuthApplicationShow } from './resources/oauth-applications';
import { PublicationList, PublicationShow, PublicationEdit, PublicationCreate, publicationsIcon } from './resources/publications';
import { FileList, FileShow, FileCreate, filesIcon } from './resources/files';
import { QueueShow } from './resources/queue';
import { MonitoringList, monitoringIcon } from './resources/monitoring';
import {
    PDFWatermarkDatabase, pdfWatermarkDatabaseIcon,
    EPUBSignDatabase, epubSignDatabaseIcon,
} from './resources/processing';
import { CustomMenu } from './CustomMenu';
import { CatalogSync } from './resources/catalog';
import { MassUpload } from './resources/mass-upload';
import { VerifyPage } from './resources/verify';
import { FingerprintVerifyPage } from './resources/fingerprint-verify';
import { CustomUserMenu } from './components/CustomUserMenu';
import { AdminDashboard } from './Dashboard';
import { BrandArchitecture, Navbar, Dropdown, DropdownList, DropdownItemField } from '@wk/components-v3-react16';
import { LoginPage } from './components/LoginPage';
import { useLocation, useNavigate } from 'react-router-dom';
import { i18nProvider } from './i18n/i18nProvider';
import { useT } from './i18n/useT';

const theme = createTheme({
    ...defaultTheme,
    palette: {
        mode: 'light',
        primary: { main: '#16324f' },
        secondary: { main: '#b56d2b' },
        background: { default: '#f5f1ea', paper: '#fffdfa' },
    },
    typography: {
        fontFamily: '"Segoe UI Variable", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    },
});

type NavItem = { labelKey: Parameters<ReturnType<typeof useT>>[0]; to: string };

const mainNavigation: NavItem[] = [
    { labelKey: 'nav.overview', to: '/' },
    { labelKey: 'nav.publications', to: '/publications' },
    { labelKey: 'nav.files', to: '/files' },
    { labelKey: 'nav.queue', to: '/queue' },
    { labelKey: 'nav.users', to: '/users' },
];

const processingItems: NavItem[] = [
    { labelKey: 'menu.pdfWatermarkDb', to: '/pdf-watermark' },
    { labelKey: 'menu.epubSignDb', to: '/epub-sign' },
];

const managementItems: NavItem[] = [
    { labelKey: 'menu.catalogSync', to: '/catalog-sync' },
    { labelKey: 'menu.massUpload', to: '/mass-upload' },
    { labelKey: 'menu.verifyPipeline', to: '/verify' },
    { labelKey: 'menu.fingerprintCheck', to: '/fingerprint-verify' },
];

const JumpstartNavbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const t = useT();
    const [processingOpen, setProcessingOpen] = useState(false);
    const [managementOpen, setManagementOpen] = useState(false);

    const chevronStyle = (open: boolean): React.CSSProperties => ({
        fontSize: '0.8em',
        display: 'inline-block',
        transition: 'transform 0.18s ease',
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    });

    const isActive = (path: string) => location.pathname === path;
    const isProcessingActive = processingItems.some(item => location.pathname === item.to);
    const isManagementActive = managementItems.some(item => location.pathname === item.to);

    return (
        <>
            <Navbar flowClass="wk-jumpstart-navbar">
                {mainNavigation.map(item => (
                    <a
                        key={item.to}
                        href={item.to}
                        slot="navbarItem"
                        onClick={event => {
                            event.preventDefault();
                            navigate(item.to);
                        }}
                        aria-current={isActive(item.to) ? 'page' : undefined}
                        style={{ textDecoration: isActive(item.to) ? 'underline' : 'none' }}
                    >
                        {t(item.labelKey)}
                    </a>
                ))}

                {/* Processing Button */}
                <button
                    id="navbar-processing-btn"
                    slot="navbarItem"
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: '8px 12px',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        textDecoration: isProcessingActive ? 'underline' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                    }}
                >
                    {t('nav.processing')}
                    <span style={chevronStyle(processingOpen)}>▼</span>
                </button>

                {/* Monitoring */}
                <a
                    href="/queue"
                    slot="navbarItem"
                    onClick={event => {
                        event.preventDefault();
                        navigate('/queue');
                    }}
                    aria-current={isActive('/queue') ? 'page' : undefined}
                    style={{ textDecoration: isActive('/queue') ? 'underline' : 'none' }}
                >
                    {t('nav.monitoring')}
                </a>

                {/* OAuth Applications */}
                <a
                    href="/oauth-applications"
                    slot="navbarItem"
                    onClick={event => {
                        event.preventDefault();
                        navigate('/oauth-applications');
                    }}
                    aria-current={isActive('/oauth-applications') ? 'page' : undefined}
                    style={{ textDecoration: isActive('/oauth-applications') ? 'underline' : 'none' }}
                >
                    {t('nav.oauthApplications')}
                </a>

                {/* Management Button */}
                <button
                    id="navbar-management-btn"
                    slot="navbarItem"
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: '8px 12px',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        textDecoration: isManagementActive ? 'underline' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                    }}
                >
                    {t('nav.management')}
                    <span style={chevronStyle(managementOpen)}>▼</span>
                </button>
            </Navbar>

            {/* Processing Dropdown - Outside Navbar */}
            <Dropdown
                targetSelector="#navbar-processing-btn"
                placementOptions={{ positions: ['bottom-left'] }}
                isOpen={processingOpen}
                onUserRequest={setProcessingOpen}
            >
                <DropdownList>
                    {processingItems.map(item => (
                        <DropdownItemField key={item.to} slot="dropdownListItem">
                            <button
                                type="button"
                                onClick={() => { setProcessingOpen(false); navigate(item.to); }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'inherit',
                                    cursor: 'pointer',
                                    padding: '8px 16px',
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit',
                                    textAlign: 'left',
                                    width: '100%',
                                }}
                            >
                                {t(item.labelKey)}
                            </button>
                        </DropdownItemField>
                    ))}
                </DropdownList>
            </Dropdown>

            {/* Management Dropdown - Outside Navbar */}
            <Dropdown
                targetSelector="#navbar-management-btn"
                placementOptions={{ positions: ['bottom-left'] }}
                isOpen={managementOpen}
                onUserRequest={setManagementOpen}
            >
                <DropdownList>
                    {managementItems.map(item => (
                        <DropdownItemField key={item.to} slot="dropdownListItem">
                            <button
                                type="button"
                                onClick={() => { setManagementOpen(false); navigate(item.to); }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'inherit',
                                    cursor: 'pointer',
                                    padding: '8px 16px',
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit',
                                    textAlign: 'left',
                                    width: '100%',
                                }}
                            >
                                {t(item.labelKey)}
                            </button>
                        </DropdownItemField>
                    ))}
                </DropdownList>
            </Dropdown>
        </>
    );
};

const CustomAppBar = () => {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const setHeight = () =>
            document.documentElement.style.setProperty('--faust-header-h', `${el.offsetHeight}px`);
        setHeight();
        const observer = new ResizeObserver(setHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <MuiAppBar
            ref={ref}
            position="fixed"
            elevation={0}
            sx={{
                zIndex: theme => theme.zIndex.drawer + 1,
                backgroundColor: 'transparent',
                color: '#16324f',
            }}
        >
            {/* Tier 1 — brand header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 56,
                    px: { xs: 1, sm: 2 },
                    backgroundColor: 'rgba(255, 255, 255, 0.92)',
                    backdropFilter: 'blur(16px)',
                    borderBottom: '1px solid rgba(22, 50, 79, 0.08)',
                }}
            >
                <SidebarToggleButton />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <BrandArchitecture
                        href="/"
                        imgSrc="https://cdn.wolterskluwer.io/wk/jumpstart-v3-assets/0.x.x/logo/wk-logo-primary-pos-large.svg"
                        imgSrcDesktop="https://cdn.wolterskluwer.io/wk/jumpstart-v3-assets/0.x.x/logo/wk-logo-primary-pos-large.svg"
                        isCompact
                        srLabel="Faust Signing Service home"
                    />
                    <Box>
                        <Box sx={{ fontSize: '0.75rem', color: 'rgba(22, 50, 79, 0.5)', lineHeight: 1 }}></Box>
                        <Box sx={{ fontSize: '1rem', fontWeight: 400, color: '#16324f' }}>Faust Signing Service</Box>
                    </Box>
                </Box>
                <Box sx={{ flex: 1 }} />
                <LoadingIndicator />
                <CustomUserMenu />
            </Box>
            {/* Tier 2 — primary navigation */}
            <Box
                sx={{
                    backgroundColor: '#16324f',
                    px: 0,
                    display: 'flex',
                    alignItems: 'stretch',
                    '& .wk-jumpstart-navbar': { width: '100%' },
                }}
            >
                <JumpstartNavbar />
            </Box>
        </MuiAppBar>
    );
};

const CustomLayout = (props: any) => (
    <Layout
        {...props}
        appBar={CustomAppBar}
        menu={CustomMenu}
        appBarAlwaysOn
        sx={{
            '& .RaLayout-appFrame': {
                marginTop: 'var(--faust-header-h, 104px)',
            },
            // Pin the Spine-style sidebar below the fixed header so it never
            // slides up underneath the navbar while the page content scrolls.
            '& .RaSidebar-fixed': {
                top: 'var(--faust-header-h, 104px)',
                height: 'calc(100vh - var(--faust-header-h, 104px))',
                backgroundColor: 'var(--cg3-color-gray-750, #232323)',
            },
            // Keep the drawer column itself dark so the rail reads as one solid surface.
            '& .RaSidebar-root .MuiPaper-root': {
                backgroundColor: 'var(--cg3-color-gray-750, #232323)',
            },
        }}
    />
);

const App = () => (
    <Admin
        authProvider={authProvider}
        dataProvider={dataProvider}
        i18nProvider={i18nProvider}
        theme={theme}
        layout={CustomLayout}
        dashboard={AdminDashboard}
        loginPage={LoginPage}
        requireAuth
        title="Faust Signing Service"
    >
        <CustomRoutes>
            <Route path="/catalog-sync" element={<CatalogSync />} />
            <Route path="/mass-upload" element={<MassUpload />} />
            <Route path="/verify" element={<VerifyPage />} />
            <Route path="/fingerprint-verify" element={<FingerprintVerifyPage />} />
        </CustomRoutes>
        {permissions => [
            <Resource
                key="publications"
                name="publications"
                list={PublicationList}
                show={PublicationShow}
                edit={permissions === 'admin' ? PublicationEdit : undefined}
                create={permissions === 'admin' ? PublicationCreate : undefined}
                icon={publicationsIcon}
                options={{ label: 'menu.publications' }}
            />,
            <Resource
                key="files"
                name="files"
                list={FileList}
                show={FileShow}
                create={permissions === 'admin' ? FileCreate : undefined}
                icon={filesIcon}
                options={{ label: 'menu.files' }}
            />,
            <Resource
                key="pdf-watermark"
                name="pdf-watermark"
                list={PDFWatermarkDatabase}
                icon={pdfWatermarkDatabaseIcon}
                options={{ label: 'menu.pdfWatermarkDb' }}
            />,
            <Resource
                key="epub-sign"
                name="epub-sign"
                list={EPUBSignDatabase}
                icon={epubSignDatabaseIcon}
                options={{ label: 'menu.epubSignDb' }}
            />,
            <Resource
                key="queue"
                name="queue"
                list={MonitoringList}
                show={QueueShow}
                icon={monitoringIcon}
                options={{ label: 'menu.monitoring' }}
            />,
            <Resource
                key="users"
                name="users"
                list={UserList}
                show={UserShow}
                edit={permissions === 'admin' ? UserEdit : undefined}
                create={permissions === 'admin' ? UserCreate : undefined}
                icon={PeopleIcon}
                options={{ label: 'menu.users' }}
            />,
            permissions === 'admin' ? (
                <Resource
                    key="invitations"
                    name="invitations"
                    list={InvitationList}
                    create={InvitationCreate}
                    show={InvitationShow}
                    icon={MailIcon}
                    options={{ label: 'menu.invitations' }}
                />
            ) : null,
            permissions === 'admin' ? (
                <Resource
                    key="oauth-applications"
                    name="oauth-applications"
                    list={OAuthApplicationList}
                    create={OAuthApplicationCreate}
                    edit={OAuthApplicationEdit}
                    show={OAuthApplicationShow}
                    icon={VpnKeyIcon}
                    options={{ label: 'menu.oauthApplications' }}
                />
            ) : null,
        ]}
    </Admin>
);

export default App;
